import { useEffect, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/lib/store'

export function AuthCallbackPage() {
  const navigate = useNavigate()
  const loadUser = useAuthStore((s) => s.loadUser)

  //   loading ▸ success ▸ needsLogin ▸ error
  const [state, setState] = useState<'loading' | 'success' | 'needsLogin' | 'error'>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    // Add a timeout to avoid infinite loading
    const loadingTimeout = setTimeout(() => {
      if (state === 'loading') {
        setState('error')
        setErrorMsg('Le traitement a pris trop de temps. Veuillez réessayer ou vous connecter manuellement.')
      }
    }, 10000) // 10 seconds timeout
    
    // Process authentication
    const processAuth = async () => {
      try {
        console.log('Processing authentication callback...')
        
        /* 1. #access_token ou ?type=magiclink */
        const { data, error } = await supabase.auth.getSessionFromUrl()
        console.log('Session from URL:', { hasSession: !!data.session, error })

        /* 2. ?code=xxxxx (flux v2) */
        if (!data.session) {
          const code = new URLSearchParams(window.location.search).get('code')
          console.log('Code from URL:', code ? 'Present' : 'Not present')
          
          if (code) {
            console.log('Exchanging code for session...')
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
            
            if (exchangeError) {
              console.error('Error exchanging code:', exchangeError)
              throw exchangeError
            }
            
            console.log('Code exchange successful, loading user...')
            await loadUser()
            setState('success')
            return
          }
        }

        /* 3. session OK */
        if (data.session) {
          console.log('Session found, loading user...')
          await loadUser()
          setState('success')
          return
        }

        /* 4. si l'e‑mail est confirmé mais pas de session */
        if (error?.message?.toLowerCase().includes('confirmed')) {
          console.log('Email confirmed but no session')
          setState('needsLogin')
          return
        }

        /* 5. échec complet */
        console.error('Auth callback error ➜', error)
        setErrorMsg(error?.message || 'Lien invalide ou expiré')
        setState('error')
      } catch (error) {
        console.error('Unexpected error during auth processing:', error)
        setErrorMsg(error instanceof Error ? error.message : 'Une erreur inattendue s\'est produite')
        setState('error')
      }
    }

    processAuth()

    // Cleanup
    return () => {
      clearTimeout(loadingTimeout)
    }
  }, [navigate, loadUser])

  // Auto-redirect after success
  useEffect(() => {
    if (state === 'success') {
      const redirectTimer = setTimeout(() => {
        navigate('/')
      }, 2000)
      
      return () => clearTimeout(redirectTimer)
    }
  }, [state, navigate])

  /* ---------- RENDU ---------- */
  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
          <p className="mt-4 text-center text-gray-600 font-medium">Traitement du lien...</p>
        </div>
      </div>
    )
  }

  if (state === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Compte confirmé !</h2>
          <p className="mt-2 text-gray-600">
            Votre compte a été créé avec succès. Vous pouvez maintenant vous connecter manuellement.
          </p>
          <div className="mt-6">
            <Link 
              to="/login"
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Se connecter
            </Link>
          </div>
          <p className="mt-4 text-sm text-gray-500">
            Vous pouvez également vous connecter via notre application mobile AEM Marketplace.
          </p>
        </div>
      </div>
    )
  }

  if (state === 'needsLogin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="mt-4 text-2xl font-bold text-gray-900">Email confirmé !</h2>
          <p className="mt-2 text-gray-600">
            Votre email est maintenant confirmé. Veuillez vous connecter pour continuer.
          </p>
          <div className="mt-6">
            <Link 
              to="/login"
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    )
  }

  /* state === 'error' */
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h2 className="mt-4 text-2xl font-bold text-gray-900">Erreur</h2>
        <p className="mt-2 text-gray-600">
          {errorMsg}
        </p>
        <div className="mt-6 space-y-3">
          <a
            href={window.location.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Ouvrir dans le navigateur
          </a>
          <div>
            <Link
              to="/login"
              className="inline-block text-primary hover:text-primary-dark font-medium"
            >
              Retour à la page de connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

